import React, { useEffect, useState, useCallback } from 'react';
import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';
import Image from 'src/components/Image';
import { getLinkDemoHost } from 'src/lib/utils';

import { Button, TextInput } from 'src/components/Inputs';
import {
  fetchTransferLinkToken,
  fetchCheckingAccounts,
} from './transfer-utils';

const onLinkSuccess = ({ token }) => {
  // All of the redirect destinations begin with `window.location.pathname`
  // to account for PR preview builds where the page path is `/commit-sha/demo`
  // as well as local and production where the page path is `/demo`
  const demoPath = window.location.pathname;
  const newLocation = demoPath + `?public_token=${token}`;
  window.location = newLocation.replaceAll('//', '/');
};

const nonRtpAccountErrMsg = 'account is not eligible for RTP network';
const rateLimitExceededErrMsg = 'rate limit exceeded';

const TransferDemo = () => {
  const Plaid = typeof window !== 'undefined' && window?.Plaid;
  const router = useRouter();
  // These will initially be empty due to https://nextjs.org/docs/advanced-features/automatic-static-optimization
  // but will eventually have values when the client renders
  const { force_flink } = router.query;
  const [publicTokenValid, setPublicTokenValid] = useState(true);
  const [legalName, setLegalName] = useState('');
  const [account, setAccount] = useState(null);
  const [transferRes, setTransferRes] = useState(null);
  const [isSubmittingTransfer, setIsSubmittingTransfer] = useState(false);

  // State related to the form
  const { handleSubmit } = useForm({
    defaultValues: {},
  });

  const { handleSubmit: handleSubmitTransfer } = useForm({
    defaultValues: {
      legalName: '',
    },
  });

  // State related to Link
  const [linkHandler, setLinkHandler] = useState(undefined);
  const isLoading = !(Plaid && linkHandler);

  // open Link
  const launchDemo = useCallback(() => {
    if (!linkHandler) return;
    linkHandler.open();
  }, [linkHandler]);

  const submitTransfer = async () => {
    const queryParameters = new URLSearchParams(window.location.search);
    const publicToken = queryParameters.get('public_token');

    setIsSubmittingTransfer(true);
    const data = {
      public_token: publicToken,
      network: 'rtp',
      legal_name: legalName,
      account_id: account.account_id,
    };
    const endpoint = new URL(
      getLinkDemoHost() + '/transfer-demo/transfer/submit',
    );
    const createTransferResp = await fetch(endpoint.href, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    const createTransferRespJson = await createTransferResp.json();
    setTransferRes(createTransferRespJson);
    setIsSubmittingTransfer(false);
  };

  // Eagerly fetch the Link Token
  useEffect(() => {
    if (!Plaid) {
      return;
    }
    setLinkHandler(undefined);

    let handler;
    // This handles the case where the inputs change
    // while a previous token is still being fetched
    let ignore = false;
    const controller = new AbortController();

    const fetchLinkToken = async () => {
      const linkTokenCreateConfig = {
        selectedCountryCode: 'US',
        selectedLanguage: 'en',
        selectedProduct: 'auth',
        isAuthTypeSelectEnabled: false,
      };
      const linkTokenResp = await fetchTransferLinkToken(
        linkTokenCreateConfig,
        {
          signal: controller.signal,
        },
      );
      if (!linkTokenResp || ignore) {
        // escape hatch for link token fetch failure
        return;
      }
      const token = linkTokenResp.linkToken;

      // create a Link handler with the provided inputs
      const configs = {
        token,
        onSuccess: (token) => {
          onLinkSuccess({
            token,
          });
        },
        onExit: async function (err) {
          if (err != null && err.error_code === 'INVALID_LINK_TOKEN') {
            // if the item_add_token becomes invalid, recreate the link
            // handler with a new item_add_token
            handler.destroy();
            handler = undefined;
            setLinkHandler(undefined);
          }
        },
      };

      handler = window?.Plaid?.create(configs, {
        enableFlexLink: force_flink,
      });
      setLinkHandler(handler);
    };
    fetchLinkToken();

    return () => {
      controller.abort();
      ignore = true;
      handler && handler.destroy();
    };
  }, [Plaid, force_flink]);

  useEffect(() => {
    const queryParameters = new URLSearchParams(window.location.search);
    const publicToken = queryParameters.get('public_token');

    if (publicToken === '' || publicToken == null) {
      return;
    }
    if (!publicTokenValid) {
      return;
    }
    const fetchAccounts = async () => {
      const endpoint = new URL(getLinkDemoHost() + '/accounts');
      endpoint.searchParams.append('public_token', publicToken);
      endpoint.searchParams.append('product', 'auth');
      endpoint.searchParams.append('use_transfer_prod_client', 'true');

      try {
        const accountResp = await fetchCheckingAccounts(publicToken);
        if (!accountResp) {
          setPublicTokenValid(false);
          return;
        }
        setAccount({
          account_id: accountResp.account_id,
          name: accountResp.name,
          mask: accountResp.mask,
        });
      } catch (e) {
        setPublicTokenValid(false);
      }
    };
    fetchAccounts();
  }, [publicTokenValid]);

  return (
    <div className='background-page-wrapper'>
      <div className='nav-spacer' />
      <div className='grid-container section-container'>
        <div className='grid-x align-justify'>
          <div className='cell small-12 page-header-breakpoint-6 large-5 page-header-content'>
            <form
              data-testid='demo-launch'
              id='demoLaunch'
              onSubmit={handleSubmit(launchDemo)}
            >
              <div className='grid-x'>
                <div className='cell small-10 page-header-breakpoint-12'>
                  <h2 className='page-header-header h2'>
                    Plaid Instant Payouts
                  </h2>
                  <h4 className='regular page-header-subheader h4'>
                    Experience instant payment to your bank account with Plaid
                  </h4>
                  {account === null && (
                    <h5 className='page-header-header h5'>
                      By launching this demo, you authorize Plaid to send an
                      instant payment to your bank account.
                    </h5>
                  )}
                </div>
              </div>
              {account === null && (
                <Button
                  type='submit'
                  className='cell medium-12 small-12 button'
                  fullWidth
                  {...(isLoading ? { disabled: true } : {})}
                >
                  Launch Instant Payouts
                </Button>
              )}
            </form>
            {account !== null && (
              <p className='regular page-header-subheader'>
                Successfully linked with the checking account {account.name}{' '}
                ending with&nbsp;
                {account.mask}.{' '}
                <a
                  role='button'
                  tabindex='0'
                  onClick={handleSubmit(launchDemo)}
                >
                  Use a different bank
                </a>
                .
              </p>
            )}
            <form
              hidden={
                account === null ||
                transferRes?.success === true ||
                (transferRes?.error != null &&
                  transferRes.error.startsWith(nonRtpAccountErrMsg)) ||
                (transferRes?.error != null &&
                  transferRes.error.startsWith(rateLimitExceededErrMsg))
              }
              onSubmit={handleSubmitTransfer(submitTransfer)}
            >
              <TextInput
                label='Your Legal Name'
                name='legalName'
                id='legalName'
                value={legalName}
                onChange={(e) => {
                  setLegalName(e.target.value);
                }}
              />
              <Button
                type='submit'
                className='cell medium-12 small-12 button'
                id='submitTransferButton'
                fullWidth
                disabled={isSubmittingTransfer || legalName === ''}
              >
                Instantly send $0.01 to my linked account
              </Button>
            </form>
            <div>
              {transferRes?.error != null &&
                !transferRes.error.startsWith(nonRtpAccountErrMsg) && (
                  <p className='regular '>
                    We are unable to complete this payout experience:&nbsp;
                    {transferRes.error}
                  </p>
                )}
              {transferRes?.error != null &&
                transferRes.error.startsWith(nonRtpAccountErrMsg) && (
                  <p className='regular '>
                    Based on&nbsp;
                    <a
                      target='_blank'
                      href='/docs/api/products/transfer/#transfercapabilitiesget'
                    >
                      /transfer/capabilities/get
                    </a>
                    ,&nbsp;this linked account is not eligible for Instant
                    Payouts over the RTP Network. The list of participating
                    financial institutions is available&nbsp;
                    <a
                      target='_blank'
                      href='https://www.theclearinghouse.org/payment-systems/rtp/rtp-participating-financial-institutions'
                      rel='noreferrer'
                    >
                      here
                    </a>
                    .{' '}
                    <a
                      role='button'
                      tabIndex='0'
                      onClick={handleSubmit(launchDemo)}
                    >
                      Try a different bank
                    </a>
                    .
                  </p>
                )}
              {transferRes?.success === true && (
                <p className='regular '>
                  Plaid successfully completed an Instant Payout of $0.01 to
                  your linked account. If the payout is not received within 30
                  minutes, please contact your financial institution.
                </p>
              )}
            </div>
          </div>
          <div className='cell small-12 medium-6 page-header-bg-wrapper'>
            <div className='page-header-bg'>
              <Image
                src='/assets/img/hero/demo-hero.png'
                alt=''
                width='1260'
                height='1028'
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransferDemo;
